import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";

// Default cinematic background — Adani Power Plant (Korba) installation.
// Override per-page via the `bgImage` prop if a different real-site photo is desired.
const DEFAULT_BG = "/project-adani-korba.png";

const TRUST_BADGES = [
  "Premium Components",
  "Nationwide Service",
  "Engineered for Reliability",
];

/**
 * Premium, full-width CTA banner used at the bottom of every page (above the
 * Footer). Copy adapts per page via `headline` & `description` props while the
 * overall visual treatment, parallax + staggered entrance animations and CTAs
 * stay consistent for brand cohesion.
 */
export default function CTABanner({
  headline = "Ready to Build with Confidence?",
  description = "From residential towers to industrial megaprojects, LIFTILE delivers reliable construction machinery engineered for performance, safety and long-term value.",
  bgImage = DEFAULT_BG,
  primaryLabel = "Request a Quote",
  primaryHref = "/contact",
  secondaryLabel = "Explore Products",
  secondaryHref = "/products",
  eyebrow = "Let's Talk",
}) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  // Subtle parallax — background drifts opposite to scroll direction
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      ref={sectionRef}
      data-testid="cta-banner"
      className="relative w-full overflow-hidden bg-liftile-black"
      style={{ minHeight: "500px" }}
    >
      {/* ------- Background image with slow zoom + scroll parallax ------- */}
      <motion.div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{ y: bgY }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center will-change-transform"
          style={{
            backgroundImage: `url(${bgImage})`,
            animation: "cta-slow-zoom 22s ease-in-out infinite alternate",
          }}
        />
      </motion.div>

      {/* ------- Cinematic dark overlay (70–75% black) ------- */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(10,10,10,0.78) 50%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      {/* ------- Soft orange lighting accents (no aggressive glow) ------- */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.22) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(20px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.18) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(24px)",
        }}
      />

      {/* ------- Top + bottom thin orange accent lines ------- */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/40 z-[2]" />
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px bg-liftile-orange/40 z-[2]" />

      {/* ------- Content ------- */}
      <div className="relative z-[3] container-x py-20 md:py-28 lg:py-32 flex flex-col items-center text-center">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="inline-flex items-center gap-3 mb-6"
        >
          <span className="block w-10 h-px bg-liftile-orange" />
          <span className="font-heading text-[10px] md:text-xs uppercase tracking-[0.32em] text-liftile-orange">
            {eyebrow}
          </span>
          <span className="block w-10 h-px bg-liftile-orange" />
        </motion.div>

        {/* Headline */}
        <motion.h2
          data-testid="cta-headline"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-5xl"
        >
          {headline}
        </motion.h2>

        {/* Description */}
        <motion.p
          data-testid="cta-description"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, delay: 0.18, ease: "easeOut" }}
          className="font-body text-liftile-cream/80 text-base md:text-lg leading-relaxed mt-7 max-w-3xl"
        >
          {description}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.36, ease: "easeOut" }}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          {primaryHref.startsWith("/") ? (
            <Link
              to={primaryHref}
              data-testid="cta-primary-btn"
              className="group relative inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm overflow-hidden transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)]"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-liftile-orange to-liftile-orange-dark opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
              <span className="relative">{primaryLabel}</span>
              <ArrowRight className="w-4 h-4 relative transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          ) : (
            <a
              href={primaryHref}
              data-testid="cta-primary-btn"
              className="group relative inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm overflow-hidden transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)]"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-liftile-orange to-liftile-orange-dark opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
              <span className="relative">{primaryLabel}</span>
              <ArrowRight className="w-4 h-4 relative transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          )}
          {secondaryHref.startsWith("/") ? (
            <Link
              to={secondaryHref}
              data-testid="cta-secondary-btn"
              className="group inline-flex items-center justify-center gap-3 border border-white/40 bg-white/[0.04] backdrop-blur-sm text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm transition-all duration-400 hover:bg-white hover:text-liftile-matte-black hover:border-white hover:-translate-y-0.5"
            >
              {secondaryLabel}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          ) : (
            <a
              href={secondaryHref}
              data-testid="cta-secondary-btn"
              className="group inline-flex items-center justify-center gap-3 border border-white/40 bg-white/[0.04] backdrop-blur-sm text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm transition-all duration-400 hover:bg-white hover:text-liftile-matte-black hover:border-white hover:-translate-y-0.5"
            >
              {secondaryLabel}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          )}
        </motion.div>

        {/* Trust badges */}
        <motion.ul
          data-testid="cta-trust-badges"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.58, ease: "easeOut" }}
          className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mt-10 md:mt-12"
        >
          {TRUST_BADGES.map((badge) => (
            <li
              key={badge}
              className="flex items-center gap-2 font-heading text-[11px] md:text-xs uppercase tracking-[0.2em] text-liftile-cream/75"
            >
              <span className="grid place-items-center w-5 h-5 rounded-full border border-liftile-orange/60 text-liftile-orange">
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
              {badge}
            </li>
          ))}
        </motion.ul>
      </div>

      <style>{`
        @keyframes cta-slow-zoom {
          0%   { transform: scale(1.00); }
          100% { transform: scale(1.08); }
        }
      `}</style>
    </section>
  );
}
